import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import { ProductFeatures } from '@/constants/ProductFeatures';
import IFeatureToggle from '@/models/auth/IFeatureToggle';
import IFeatureToggles from '@/models/auth/IFeatureToggles';
import DateTimeUtils from './DateTimeUtils';
import JWTUtils from './JWTUtils';
import theLocalStorageUtils from './LocalStorageUtils';

/**
 * Utility methods for checking product feature enablements.
 * Currently these enablements are provided within the QS access token (JWT).
 */
export default {
    /**
     * Checks for MLS feature enabled
     * @returns
     */
    isMLSEnabled(): boolean {
        const accessToken = getAccessToken();
        if (accessToken) {
            const featureToggles = getFeatureToggles(accessToken);
            const enabled = isEnabled(featureToggles, ProductFeatures.MLS);
            return enabled;
        }
        return false;
    },

    /**
     * Checks for Smart Data feature enabled
     * @returns
     */
    isSmartDataEnabled(): boolean {
        const accessToken = getAccessToken();
        if (accessToken) {
            const featureToggles = getFeatureToggles(accessToken);
            const enabled = isEnabled(featureToggles, ProductFeatures.SmartData);
            return enabled;
        }
        return false;
    },

    /**
     * Checks for Chat feature enabled
     * @returns
     */
    isChatEnabled(): boolean {
        const accessToken = getAccessToken();
        if (accessToken) {
            const featureToggles = getFeatureToggles(accessToken);
            const enabled = isEnabled(featureToggles, ProductFeatures.Chat);
            return enabled;
        }
        return false;
    }
};

function getAccessToken(): string | null {
    const accessToken = theLocalStorageUtils.get(LocalStorageKeys.qsAccessToken);
    return accessToken;
}

/**
 * Get the feature toggles from the given access token (JWT).
 * @param accessToken
 * @returns
 */
function getFeatureToggles(accessToken: string): IFeatureToggles {
    const featureToggles: IFeatureToggles = [];
    const featureList = JWTUtils.getValue(accessToken, 'featureIds');
    const features = featureList.split(',');
    for (const feature of features) {
        const featureToggle = buildFeatureToggle(feature);
        if (featureToggle) {
            featureToggles.push(featureToggle);
        }
    }

    return featureToggles;
}

/**
 * Build a feature toggle from the combined feature toggle values (pipe delimited).
 * Example: "3d0e9777-92a6-4048-87b1-d42d3b2b5379|True|12/31/9999 11:59:59 PM"
 * @param feature
 * @returns
 */
function buildFeatureToggle(feature: string): IFeatureToggle | null {
    const featureParts = feature.split('|');
    if (featureParts.length >= 3) {
        const endDate = DateTimeUtils.fromFormat(featureParts[2], 'MM/DD/YYYY LTS');

        const featureToggle: IFeatureToggle = {
            featureId: featureParts[0],
            isEnabled: featureParts[1].toLowerCase() === 'true',
            endDate: endDate
        };
        return featureToggle;
    }
    return null;
}

/**
 * Determine if a feature toggle is enabled from the given feature toggles and given product feature.
 * @param featureToggles
 * @param feature
 * @returns
 */
function isEnabled(featureToggles: IFeatureToggles, feature: ProductFeatures): boolean {
    const featureToggle = featureToggles.find(ft => ft.featureId.toLowerCase() == feature);
    if (featureToggle) {
        return featureToggle.isEnabled;
    }
    return false;
}
